import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Phone, CheckCircle, MessageSquare } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhoneVerificationPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter phone, 2: Enter code, 3: Success
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverCode, setServerCode] = useState(''); // For testing

  const handleSendCode = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.startsWith('+')) {
      toast.error('Phone number must include country code (e.g., +1234567890)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const res = await axios.post(
        `${API}/phone/send-code`,
        {
          phone_number: phoneNumber,
          user_id: user.id
        },
        config
      );

      // For testing - code is returned
      if (res.data.code) {
        setServerCode(res.data.code);
      }

      setStep(2);
      toast.success('Verification code sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (verificationCode.length !== 6) {
      toast.error('Please enter 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API}/phone/verify-code`,
        {
          phone_number: phoneNumber,
          code: verificationCode
        },
        config
      );

      setStep(3);
      toast.success('Phone number verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/crypto/settings')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </Button>

        {/* Step 1: Enter Phone Number */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <Phone className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <CardTitle className="text-2xl text-center">Verify Your Phone</CardTitle>
              <CardDescription className="text-center">
                Add your phone number to enable phone-based transfers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1234567890"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Include country code (e.g., +1 for USA, +44 for UK)
                  </p>
                </div>

                <Alert>
                  <MessageSquare className="h-4 w-4" />
                  <AlertDescription>
                    We'll send you a 6-digit code via SMS to verify your number
                  </AlertDescription>
                </Alert>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  size="lg"
                >
                  {loading ? 'Sending Code...' : 'Send Verification Code'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Enter Verification Code */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <MessageSquare className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <CardTitle className="text-2xl text-center">Enter Verification Code</CardTitle>
              <CardDescription className="text-center">
                We sent a 6-digit code to {phoneNumber}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyCode} className="space-y-4">
                {serverCode && (
                  <Alert className="bg-yellow-50 border-yellow-300">
                    <AlertDescription className="text-yellow-800 text-sm">
                      <strong>Testing Mode:</strong> Your code is: <strong className="text-xl">{serverCode}</strong>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    required
                    className="text-center text-2xl tracking-widest"
                  />
                  <p className="text-xs text-gray-500 text-center">
                    Enter the 6-digit code
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  size="lg"
                >
                  {loading ? 'Verifying...' : 'Verify Phone Number'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="w-full"
                >
                  Change Phone Number
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card>
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                Phone Verified!
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Your phone number {phoneNumber} has been successfully verified.
              </p>

              <div className="space-y-4 mb-8">
                <Alert className="bg-green-50 border-green-300">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    You can now receive crypto transfers via your phone number!
                  </AlertDescription>
                </Alert>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/crypto/wallet')}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  Go to Wallet
                </Button>
                <Button
                  onClick={() => navigate('/crypto/settings')}
                  variant="outline"
                  className="w-full"
                >
                  Back to Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PhoneVerificationPage;
